import React, { FC, ReactNode, useMemo, useState } from 'react';

import { keyframes, styled, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { scroller } from 'react-scroll';

import { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import QuestionQuiz, {
  QuestionFooterProps,
  QuestionHeaderProps,
} from '~/components/QuestionQuiz/QuestionQuiz';
import QuestionSlideHeader from '~/components/QuestionQuiz/QuestionSlideHeader';
import {
  AnswerButton,
  Buttons,
  NavigationButton,
  StyledQuestionMarkIcon,
} from '~/components/QuestionQuiz/styles';
import useQuestions from '~/components/QuestionQuiz/useQuestions';
import useTemporalVisibility from '~/hooks/animations/useTemporalVisibility';
import useReport from '~/hooks/useReport';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useCheckQuizAnswers from '~/pages/SingleVideoPage/PostVideoQuestion/useCheckQuizAnswers';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { QUESTION_TYPES } from '~/pages/const';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX, useIsWidthUp } from '~/theme';
import { CustomerSupportRequestEntityType, QuizQuestion } from '~/types';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

interface IPostVideoQuestion {
  videoId?: string;
  renderButton: ReactNode;
  renderNavigation: ReactNode;
  question: QuizQuestion;
  continueLearning?: () => void;
}

const congratsKeys = [
  'outstanding',
  'brilliant',
  'perfect',
  'fantasticJob',
  'impressive',
  'excellent',
  'greatWork',
  'amazing',
  'wellDone',
  'superb',
];

const CONGRATS_TOAST_FREEZE_DURATION = 5000;

const PostVideoQuestion: FC<IPostVideoQuestion> = ({
  videoId,
  renderButton,
  renderNavigation,
  question,
  continueLearning,
}) => {
  const theme = useTheme();
  const isMobile = !useIsWidthUp('md');
  const questionId = question?.id;
  const type = question?.frontend_type as QUESTION_TYPES;
  const { t } = useTenantTranslation();
  const { selectedAnswersIdsByQuestionIdMap, collectAnswersIdsByQuestionId, duration } =
    useQuestions();
  const [congratsMessage, setCongratsMessage] = useState('');
  const { visible: toastVisible, show: showToast } = useTemporalVisibility(
    CONGRATS_TOAST_FREEZE_DURATION,
  );
  const { submittedQuestionsIds, isCorrectByIdMap, checkQuizAnswers } = useCheckQuizAnswers({
    duration,
    questionId,
    type,
    selectedAnswersIds: selectedAnswersIdsByQuestionIdMap[questionId],
    videoId,
    onSuccess: () => {
      const validChance = Math.random() < 0.75;
      if (!validChance) {
        return;
      }

      const keyIndex = Math.floor(Math.random() * congratsKeys.length);
      const key = congratsKeys[keyIndex];
      setCongratsMessage(t(`page.video.congratulations.${key}`));
      showToast();
    },
  });
  const controlledReport = useReport(CustomerSupportRequestEntityType.QuizesQuestions);

  const questions = useMemo(() => [question], [question]);

  const questionHeader = ({ openReport, infoLabel }: QuestionHeaderProps) => (
    <StyledQuestionSlideHeader
      caption={t('common.knowledgeCheckpoint')}
      info={infoLabel}
      handleReportClick={() => {
        sendAnalyticEvent(VIDEO_PAGE_EVENTS.report);
        openReport();
      }}
    />
  );

  const questionFooter = ({ question, isLastQuestion, goToExplanation }: QuestionFooterProps) => {
    const questionSubmitted = submittedQuestionsIds.includes(question.id);
    const hasSelected = selectedAnswersIdsByQuestionIdMap[question.id]?.length > 0;

    const handleExplanationClick = () => {
      if (!goToExplanation) {
        return;
      }

      sendAnalyticEvent(VIDEO_PAGE_EVENTS.questionExplainer);
      goToExplanation();
      if (isMobile) {
        scroller.scrollTo('explanationScreen', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuint',
          offset: -100,
        });
      }
    };

    const submitAnswer = () => {
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.submitPostVideoAnswer);

      const selectedAnswers = questions
        .filter((question) => selectedAnswersIdsByQuestionIdMap[question.id]?.length)
        .map((question) => ({
          id: question.id,
          type,
          answer: selectedAnswersIdsByQuestionIdMap[questionId],
        }));

      if (isLastQuestion) {
        checkQuizAnswers(selectedAnswers);
        return;
      }
    };

    return (
      <Buttons>
        {!!goToExplanation && (
          <StyledNavigationButton
            size="large"
            data-testid="seeWhy"
            label={t('page.video.why')}
            icon={
              <StyledQuestionMarkIcon
                color={
                  !questionSubmitted ? theme.palette.common.white : theme.palette.common.primary
                }
              />
            }
            onClick={handleExplanationClick}
            disabled={!questionSubmitted}
          />
        )}
        {questionSubmitted ? (
          renderButton
        ) : (
          <StyledAnswerButton
            data-testid="buttonBeforeSubmit"
            disabled={!hasSelected}
            onClick={submitAnswer}
          >
            {t('button.answer')}
          </StyledAnswerButton>
        )}
      </Buttons>
    );
  };

  return (
    <>
      <QuestionQuiz
        submittedQuestionsIds={submittedQuestionsIds}
        renderNavigation={renderNavigation}
        isCorrectByIdMap={isCorrectByIdMap}
        selectedAnswersIdsByQuestionIdMap={selectedAnswersIdsByQuestionIdMap}
        collectAnswersIdsByQuestionId={collectAnswersIdsByQuestionId}
        questions={questions}
        renderQuestionHeader={questionHeader}
        renderQuestionFooter={questionFooter}
        continueLearning={continueLearning}
        quizAnalyticsEvents={VIDEO_PAGE_EVENTS}
        controlledReport={controlledReport}
        colorsMode={QuizColorsMode.PostVideo}
      />
      <AnimatePresence>
        {congratsMessage && toastVisible && (
          <ToastWrapper>
            <ToastContainer>
              <CongratsToast
                initial={{
                  opacity: 0,
                  bottom: -50,
                }}
                animate={{
                  opacity: 1,
                  bottom: isMobile ? 0 : 60,
                }}
                exit={{
                  opacity: 0,
                  bottom: -50,
                }}
                transition={{ delay: 0.6 }}
              >
                <PopAnimation>{congratsMessage}</PopAnimation>
              </CongratsToast>
            </ToastContainer>
          </ToastWrapper>
        )}
      </AnimatePresence>
    </>
  );
};

export default PostVideoQuestion;

const CongratsToast = styled(motion.div)`
  position: absolute;
`;
const pop = keyframes`
    0% {
        transform: scale(0.8);
    }
    20% {
        transform: scale(1.2);
    }
    29% {
        transform: scale(0.9);
    }
    34% {
        transform: scale(1.1);
    }
    39% {
        transform: scale(0.95);
    }
    43% {
        transform: scale(1);
    }
    46% {
        transform: scale(0.98);
    }
    48% {
        transform: scale(1);
    }
`;
const PopAnimation = styled('span')`
  display: block;
  min-width: 200px;
  padding: ${TINY_MARGIN_PX} ${SMALL_MARGIN_PX};
  font-weight: 700;
  text-align: center;
  border-radius: ${TINY_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.green};
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  border: 2px solid ${({ theme }) => theme.palette.common.green};
  animation-name: ${pop};
  animation-duration: 3s;
  animation-delay: 0.6s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;
const ToastWrapper = styled('div')`
  position: absolute;
  bottom: 0;
  width: calc(100% - 48px);
  margin: 0 ${SUBMIDDLE_MARGIN_PX};
  padding: 0 ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 ${SUBMIDDLE_MARGIN_PX};
  }
`;
const ToastContainer = styled('div')`
  display: flex;
  justify-content: end;
  position: relative;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up('md')} {
    justify-content: center;
  }
`;
const StyledNavigationButton = styled(NavigationButton)`
  & .buttonLabel {
    color: ${({ theme }) => theme.palette.common.text.white};
  }
`;
export const StyledAnswerButton = styled(AnswerButton)`
  &.btnContainedPrimary:hover {
    color: ${({ theme }) => theme.palette.common.text.white};
  }
`;
const StyledQuestionSlideHeader = styled(QuestionSlideHeader)`
  color: ${({ theme }) => theme.palette.common.text.secondaryToWhite};
`;
